import { Modal, notification } from "antd";
import { get, isEmpty } from "lodash";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

const VAULT_UNREADABLE = "ENCRYPTION_VAULT_UNREADABLE";

const baseUrl = process.env.REACT_APP_BASE_URL;

const codeOf = (error) => get(error, "graphQLErrors.0.message");

export const run = (mutate, options) =>
  new Promise((resolve, reject) =>
    mutate({ ...options, onCompleted: resolve, onError: reject }),
  );

const confirmDanger = (labels) =>
  new Promise((resolve) =>
    Modal.confirm({
      title: labels.TITLE,
      content: labels.TEXT,
      okText: labels.CONFIRM,
      cancelText: labels.CANCEL,
      okButtonProps: { danger: true },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    }),
  );

export const notifyFailure = (labels) => (error) => {
  notification.error({
    message: get(labels.ERRORS, codeOf(error), labels.ERRORS.DEFAULT),
  });
  return null;
};

export const useEncryption = ({ where, skip, labels, onChanged }) => {
  const query = useQuery(graphql.queries.ORGANIZATION_ENCRYPTION, {
    variables: { where },
    skip,
    fetchPolicy: "network-only",
  });
  const [linkKey, { loading: linking }] = useMutation(
    graphql.mutations.LINK_ORGANIZATION_KEY,
  );
  const [prepareImport, { loading: preparing }] = useMutation(
    graphql.mutations.PREPARE_ORGANIZATION_KEY_IMPORT,
  );
  const [importKey, { loading: importing }] = useMutation(
    graphql.mutations.IMPORT_ORGANIZATION_KEY,
  );
  const [verifyKey, { loading: verifying }] = useMutation(
    graphql.mutations.VERIFY_ORGANIZATION_KEY,
  );
  const [sendHandover, { loading: sending }] = useMutation(
    graphql.mutations.SEND_ORGANIZATION_KEY_HANDOVER,
  );

  const encryption = get(query, "data.organizationEncryption") || {};

  const failed = (error) => {
    const code = codeOf(error);
    notification.error({
      message: get(labels.ERRORS, code, labels.ERRORS.DEFAULT).replace(
        "{{region}}",
        encryption.region || "",
      ),
    });
    return null;
  };

  const changed = (result) =>
    Promise.all([query.refetch(), onChanged()]).then(() => result);

  const activate = (mutate, variables, field) => {
    const attempt = (replace) =>
      run(mutate, { variables: { ...variables, where, replace } }).then(
        (data) => changed(get(data, field)),
      );
    return attempt(false)
      .catch((error) => {
        if (codeOf(error) !== VAULT_UNREADABLE) throw error;
        return confirmDanger(labels.REPLACE).then((confirmed) =>
          confirmed ? attempt(true) : null,
        );
      })
      .catch(failed);
  };

  return {
    encryption,
    loading: query.loading,
    busy: linking || preparing || importing || verifying || sending,
    onLink: (keyArn) => activate(linkKey, { keyArn }, "linkOrganizationKey"),
    onPrepare: () =>
      run(prepareImport, { variables: { where } })
        .then(
          ({ prepareOrganizationKeyImport }) => prepareOrganizationKeyImport,
        )
        .catch(failed),
    onImport: ({ encryptedKeyMaterial, expiresAt }) =>
      activate(
        importKey,
        { encryptedKeyMaterial, expiresAt },
        "importOrganizationKey",
      ),
    onVerify: () =>
      run(verifyKey, { variables: { where } })
        .then(({ verifyOrganizationKey }) => changed(verifyOrganizationKey))
        .catch(failed),
    onSendHandover: (recipient) =>
      run(sendHandover, { variables: { where, recipient, baseUrl } })
        .then(({ sendOrganizationKeyHandover }) =>
          changed(sendOrganizationKeyHandover),
        )
        .catch(failed),
  };
};

export const useApiAccess = ({ where, skip, labels }) => {
  const query = useQuery(graphql.queries.ORGANIZATION_API_ACCESS, {
    variables: { where },
    skip,
    fetchPolicy: "network-only",
  });
  const [setAccess, { loading: switching }] = useMutation(
    graphql.mutations.SET_ORGANIZATION_API_ACCESS,
  );
  const [createCredential, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_ORGANIZATION_API_CREDENTIAL,
  );
  const [deleteCredential, { loading: deleting }] = useMutation(
    graphql.mutations.DELETE_ORGANIZATION_API_CREDENTIAL,
  );
  const [resendCredential, { loading: resending }] = useMutation(
    graphql.mutations.RESEND_ORGANIZATION_API_CREDENTIAL,
  );

  const access = get(query, "data.organizationApiAccess") || {};
  const failed = notifyFailure(labels);
  const refreshed = (result) => query.refetch().then(() => result);

  const toggle = (enabled) =>
    run(setAccess, { variables: { where, enabled } })
      .then(refreshed)
      .catch(failed);

  const remove = (credential) =>
    run(deleteCredential, {
      variables: { where, credential: { id: credential.id } },
    })
      .then(refreshed)
      .catch(failed);

  return {
    access,
    loading: query.loading,
    busy: switching || creating || deleting || resending,
    onToggle: (enabled) =>
      enabled || isEmpty(access.credentials)
        ? toggle(enabled)
        : confirmDanger(labels.DISABLE).then((confirmed) =>
            confirmed ? toggle(false) : null,
          ),
    onCreate: (data) =>
      run(createCredential, { variables: { where, data, baseUrl } })
        .then(({ createOrganizationApiCredential }) =>
          refreshed(createOrganizationApiCredential),
        )
        .catch(failed),
    onDelete: (credential) =>
      confirmDanger(labels.DELETE).then((confirmed) =>
        confirmed ? remove(credential) : null,
      ),
    onResend: (credential, recipient) =>
      run(resendCredential, {
        variables: {
          where,
          credential: { id: credential.id },
          recipient,
          baseUrl,
        },
      })
        .then(refreshed)
        .catch(failed),
  };
};

export const useEnvironment = ({ where, skip }) => {
  const query = useQuery(graphql.queries.ORGANIZATION_ENVIRONMENT, {
    variables: { where },
    skip,
    fetchPolicy: "network-only",
  });
  const [setEnvironment, { loading: saving }] = useMutation(
    graphql.mutations.SET_ORGANIZATION_ENVIRONMENT,
  );

  return {
    url: get(query, "data.organizationEnvironment.url") || "",
    ready: Boolean(get(query, "data.organizationEnvironment.ready")),
    loading: query.loading,
    busy: saving,
    onSave: (data) =>
      run(setEnvironment, { variables: { where, data } }).then(() =>
        query.refetch(),
      ),
  };
};
